/** Versions tab (design spec §9): history list + detail diff. Row actions
 * (tag/export/restore/archive/delete) and Import are layered on by the
 * action components in this directory. */
import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { ChevronDown, ChevronRight } from 'lucide-react'
import {
  Badge,
  Button,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalTitle,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui'
import { useCanAccessFeature, usePermission } from '@/hooks'
import { apiClient } from '@/services/apiClient'
import { useWorkflowConfig } from '@/hooks/useWorkflowConfig'
import {
  downloadWorkflowVersionExport,
  useArchiveWorkflowVersion,
  useDeleteWorkflowVersion,
  useRemoveWorkflowTag,
  useRestoreWorkflowVersion,
  useWorkflowVersions,
  VersionDeleteBlockedError,
} from '@/hooks/useWorkflowVersions'
import type { WorkflowOverlayJson } from '@/types/workflowConfig'
import { CANDIDATE_TAG, type WorkflowVersionRow } from '@/types/workflowVersions'
import { diffOverlayRefs } from './versionsDiff'
import { TagPopover } from './TagPopover'
import { ImportVersionDialog } from './ImportVersionDialog'
import { PromoteCandidateDialog } from './PromoteCandidateDialog'

const COLLAPSE_LIMIT = 30

function relTime(iso: string | null): string {
  if (!iso) return '—'
  const ms = Date.now() - new Date(iso).getTime()
  const days = Math.floor(ms / 86_400_000)
  if (days === 0) return 'today'
  if (days === 1) return 'yesterday'
  return `${days} days ago`
}

function OriginBadge({ row }: { row: WorkflowVersionRow }) {
  if (row.origin === 'imported') {
    const src = row.provenance?.imported_from
    const title = `Imported from ${src?.source_label ?? 'another environment'}${
      src?.version_number != null ? ` v${src.version_number}` : ''
    }`
    return <Badge variant="info" title={title}>Imported</Badge>
  }
  if (row.origin === 'seeded') return <Badge variant="secondary">Seeded</Badge>
  return <Badge variant="outline">Published</Badge>
}

/** Promotion evidence note (spec §9/AC4) — mirrors the OriginBadge pattern:
 * a pill whose `title` carries the audit detail (run reference + date). */
function PromotedBadge({ row }: { row: WorkflowVersionRow }) {
  const promoted = row.provenance?.promoted
  if (!promoted) return null
  const refParts = [
    promoted.run_url ? `run ${promoted.run_url}` : null,
    promoted.run_id ? `id ${promoted.run_id}` : null,
  ].filter((part): part is string => part != null)
  const ref = refParts.length > 0 ? refParts.join(', ') : 'an unrecorded run'
  const title = `Promoted from ${ref} on ${promoted.promoted_at}`
  return (
    <Badge variant="info" title={title} data-testid="promoted-badge">
      Promoted
    </Badge>
  )
}

function RowActions({
  workflowId,
  row,
  allRows,
  canConfigure,
  isPlatform,
}: {
  workflowId: string
  row: WorkflowVersionRow
  allRows: WorkflowVersionRow[]
  canConfigure: boolean
  isPlatform: boolean
}) {
  const canDelete = usePermission('workflows:versions:delete')
  const canPublish = usePermission('workflows:publish')
  const [confirm, setConfirm] = useState<'restore' | 'delete' | 'discard' | null>(null)
  const [serverBlockers, setServerBlockers] = useState<string[]>([])
  const [exportState, setExportState] = useState<'idle' | 'busy' | 'error'>('idle')
  const [promoteOpen, setPromoteOpen] = useState(false)
  const isCandidate = row.tags.includes(CANDIDATE_TAG)
  const restore = useRestoreWorkflowVersion(workflowId)
  const archive = useArchiveWorkflowVersion(workflowId)
  const del = useDeleteWorkflowVersion(workflowId)
  const removeTag = useRemoveWorkflowTag(workflowId)

  const blockerText = row.delete_blockers.map((b) => b.message).join('; ')
  const deleteBlocked = row.delete_blockers.length > 0

  const handleExport = async () => {
    setExportState('busy')
    try {
      await downloadWorkflowVersionExport(workflowId, row.version_number)
      setExportState('idle')
    } catch {
      setExportState('error')
    }
  }

  return (
    <span className="ml-auto flex items-center gap-1">
      {isPlatform && (
        <>
          <Button size="sm" variant="outline" disabled={exportState === 'busy'} onClick={handleExport}>
            Export
          </Button>
          {exportState === 'error' && (
            <span className="text-xs text-destructive">Export failed</span>
          )}
        </>
      )}
      {canConfigure && (
        <>
          <TagPopover workflowId={workflowId} row={row} allRows={allRows} />
          {/* The reserved candidate tag's remove action is the Discard flow
              below — suppress the generic per-tag untag button for it. */}
          {row.tags.filter((tag) => tag !== CANDIDATE_TAG).map((tag) => (
            <Button
              key={tag}
              size="sm"
              variant="outline"
              disabled={removeTag.isPending}
              onClick={() => removeTag.mutate(tag)}
            >
              Untag '{tag}'
            </Button>
          ))}
          {isCandidate && (
            <>
              {!row.is_current && canPublish && (
                <Button size="sm" variant="outline" onClick={() => setPromoteOpen(true)}>
                  Promote
                </Button>
              )}
              <Button size="sm" variant="outline" onClick={() => setConfirm('discard')}>
                Discard
              </Button>
            </>
          )}
          {!row.is_current && !row.archived_at && (
            <Button size="sm" variant="outline" onClick={() => setConfirm('restore')}>
              Make current
            </Button>
          )}
          {!row.is_current && row.tags.length === 0 && (
            <Button
              size="sm"
              variant="outline"
              disabled={archive.isPending}
              isLoading={archive.isPending}
              onClick={() =>
                archive.mutate({ versionNumber: row.version_number, archive: !row.archived_at })
              }
            >
              {row.archived_at ? 'Unarchive' : 'Archive'}
            </Button>
          )}
          {canDelete && !row.is_current && (
            deleteBlocked ? (
              // Disabled buttons carry `disabled:pointer-events-none` and drop
              // out of the tab order, so a `title` on the button itself is
              // unreachable by hover OR keyboard. Wrap in a focusable span and
              // let the design system's Tooltip (hover + focus) surface the
              // blocker text instead.
              <Tooltip>
                <TooltipTrigger asChild>
                  <span tabIndex={0} data-testid="delete-tooltip-trigger" className="inline-flex">
                    <Button size="sm" variant="outline" disabled>
                      Delete
                    </Button>
                  </span>
                </TooltipTrigger>
                <TooltipContent>{blockerText}</TooltipContent>
              </Tooltip>
            ) : (
              <Button size="sm" variant="outline" onClick={() => setConfirm('delete')}>
                Delete
              </Button>
            )
          )}
        </>
      )}
      {confirm === 'restore' && (
        <Modal open onOpenChange={(o) => { if (!o) setConfirm(null) }}>
          <ModalContent size="sm" showClose={false}>
            <ModalHeader><ModalTitle>Make v{row.version_number} current?</ModalTitle></ModalHeader>
            <ModalBody>
              <p className="text-sm">
                This version's customization also becomes the published customization, so
                product updates keep it after redeploys.
              </p>
            </ModalBody>
            <ModalFooter>
              <Button variant="outline" onClick={() => setConfirm(null)}>Cancel</Button>
              <Button
                disabled={restore.isPending}
                isLoading={restore.isPending}
                onClick={() =>
                  restore.mutate(row.version_number, { onSuccess: () => setConfirm(null) })
                }
              >
                Restore
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      )}
      {confirm === 'delete' && (
        <Modal open onOpenChange={(o) => { if (!o) { setConfirm(null); setServerBlockers([]) } }}>
          <ModalContent size="sm" showClose={false}>
            <ModalHeader><ModalTitle>Delete v{row.version_number} permanently?</ModalTitle></ModalHeader>
            <ModalBody className="space-y-2">
              <p className="text-sm">This cannot be undone.</p>
              {serverBlockers.map((msg) => (
                <p key={msg} className="text-sm text-destructive">{msg}</p>
              ))}
            </ModalBody>
            <ModalFooter>
              <Button variant="outline" onClick={() => { setConfirm(null); setServerBlockers([]) }}>Cancel</Button>
              <Button
                variant="destructive"
                disabled={del.isPending}
                isLoading={del.isPending}
                onClick={() =>
                  del.mutate(row.version_number, {
                    onSuccess: () => { setConfirm(null); setServerBlockers([]) },
                    onError: (err) => {
                      if (err instanceof VersionDeleteBlockedError) {
                        setServerBlockers(err.blockers.map((b) => b.message))
                      }
                    },
                  })
                }
              >
                Delete version
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      )}
      {confirm === 'discard' && (
        <Modal open onOpenChange={(o) => { if (!o) setConfirm(null) }}>
          <ModalContent size="sm" showClose={false}>
            <ModalHeader><ModalTitle>Discard the candidate on v{row.version_number}?</ModalTitle></ModalHeader>
            <ModalBody>
              <p className="text-sm">
                Drops the pending candidate tag; the currently promoted version stays live.
              </p>
            </ModalBody>
            <ModalFooter>
              <Button variant="outline" onClick={() => setConfirm(null)}>Cancel</Button>
              <Button
                variant="destructive"
                disabled={removeTag.isPending}
                isLoading={removeTag.isPending}
                onClick={() =>
                  removeTag.mutate(CANDIDATE_TAG, { onSuccess: () => setConfirm(null) })
                }
              >
                Discard candidate
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      )}
      {promoteOpen && (
        <PromoteCandidateDialog workflowId={workflowId} onClose={() => setPromoteOpen(false)} />
      )}
    </span>
  )
}

export function VersionsTab({
  workflowId,
  isPlatformOrder,
  isPlatform,
}: {
  workflowId: string
  isPlatformOrder: boolean
  isPlatform: boolean
}) {
  const [showArchived, setShowArchived] = useState(false)
  const [showAll, setShowAll] = useState(false)
  const [selected, setSelected] = useState<number | null>(null)
  const [importOpen, setImportOpen] = useState(false)
  const canConfigure = useCanAccessFeature('workflow_configuration', 'workflows:configure')
  const versions = useWorkflowVersions(workflowId, showArchived)
  // Current published overlay for the detail diff — only meaningful (and
  // only queryable) for platform order workflows.
  const config = useWorkflowConfig(isPlatformOrder ? workflowId : undefined)

  if (versions.isLoading) {
    return <p className="py-4 text-sm text-muted-foreground">Loading versions…</p>
  }
  if (versions.isError || !versions.data) {
    return <p className="py-4 text-sm text-destructive">Couldn't load version history.</p>
  }

  const rows = showAll ? versions.data : versions.data.slice(0, COLLAPSE_LIMIT)

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <label className="flex items-center gap-2 text-sm text-muted-foreground">
          <input
            type="checkbox"
            checked={showArchived}
            onChange={(e) => setShowArchived(e.target.checked)}
            aria-label="Show archived versions"
          />
          Show archived
        </label>
        <div data-testid="versions-toolbar">
          {isPlatformOrder && canConfigure && (
            <Button size="sm" variant="outline" onClick={() => setImportOpen(true)}>
              Import
            </Button>
          )}
        </div>
      </div>

      <ul className="divide-y divide-border rounded-md border border-border">
        {rows.map((row) => {
          const expanded = selected === row.version_number
          return (
            <li
              key={row.id}
              data-testid="version-row"
              className={`flex flex-wrap items-center gap-2 px-3 py-2 text-sm ${
                row.archived_at ? 'opacity-60' : ''
              }`}
            >
              <button
                type="button"
                aria-expanded={expanded}
                className="inline-flex items-center gap-0.5 font-mono font-medium"
                onClick={() => setSelected(expanded ? null : row.version_number)}
              >
                {expanded ? (
                  <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" aria-hidden />
                ) : (
                  <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" aria-hidden />
                )}
                v{row.version_number}
              </button>
              {row.is_current && (
                <span className="rounded-full bg-success/10 px-2 py-0.5 text-xs text-success">
                  Current
                </span>
              )}
              <OriginBadge row={row} />
              <PromotedBadge row={row} />
              {row.base_version_id != null && (
                <Badge
                  variant="info"
                  title="Carries a customization on top of the platform base"
                >
                  Customized
                </Badge>
              )}
              {row.tags.map((tag) =>
                tag === CANDIDATE_TAG ? (
                  <Badge
                    key={tag}
                    data-testid="candidate-badge"
                    className="border-amber-500/40 bg-amber-500/15 text-amber-700 dark:text-amber-400"
                  >
                    candidate
                  </Badge>
                ) : (
                  <Badge key={tag} variant="default">{tag}</Badge>
                ),
              )}
              {row.archived_at && <Badge variant="warning">Archived</Badge>}
              <span className="text-muted-foreground">{relTime(row.published_at)}</span>
              {row.created_by_name && (
                <span className="text-muted-foreground">{row.created_by_name}</span>
              )}
              <RowActions
                workflowId={workflowId}
                row={row}
                allRows={versions.data}
                canConfigure={canConfigure}
                isPlatform={isPlatform}
              />
              {expanded && (
                // w-full wraps the panel onto its own line inside the
                // flex-wrap row, so it expands directly under the version
                // it belongs to (long histories made a below-the-list
                // panel invisible off-screen).
                <div className="w-full">
                  <VersionDetail
                    workflowId={workflowId}
                    row={row}
                    currentOverlay={isPlatformOrder ? (config.data?.published ?? null) : null}
                    isPlatformOrder={isPlatformOrder}
                  />
                </div>
              )}
            </li>
          )
        })}
      </ul>

      {!showAll && versions.data.length > COLLAPSE_LIMIT && (
        <button
          type="button"
          className="text-sm text-primary underline"
          onClick={() => setShowAll(true)}
        >
          Show all {versions.data.length} versions
        </button>
      )}

      {importOpen && (
        <ImportVersionDialog workflowId={workflowId} onClose={() => setImportOpen(false)} />
      )}
    </div>
  )
}

/** The list row carries no overlay body — fetch the selected version's
 * overlay_snapshot lazily from the existing per-version detail endpoint. */
function useVersionOverlay(
  workflowId: string,
  row: WorkflowVersionRow,
): WorkflowOverlayJson | null | undefined {
  const query = useQuery({
    queryKey: ['workflows', 'version-detail', workflowId, row.version_number],
    queryFn: () =>
      apiClient.get<{ overlay_snapshot: WorkflowOverlayJson | null }>(
        `/api/workflows/${workflowId}/versions/${row.version_number}`,
      ),
    enabled: !!workflowId,
    staleTime: 60_000,
  })
  return query.data?.overlay_snapshot
}

function VersionDetail({
  workflowId,
  row,
  currentOverlay,
  isPlatformOrder,
}: {
  workflowId: string
  row: WorkflowVersionRow
  currentOverlay: WorkflowOverlayJson | null
  isPlatformOrder: boolean
}) {
  return (
    <div data-testid="version-detail" className="rounded-md border border-border p-3 text-sm">
      <p className="font-medium">Version v{row.version_number}</p>
      <p className="text-muted-foreground">
        Created {row.created_at} · {row.created_by_name ?? 'platform'}
      </p>
      {isPlatformOrder && (
        <VersionOverlayDiff
          workflowId={workflowId}
          row={row}
          currentOverlay={currentOverlay}
        />
      )}
    </div>
  )
}

function VersionOverlayDiff({
  workflowId,
  row,
  currentOverlay,
}: {
  workflowId: string
  row: WorkflowVersionRow
  currentOverlay: WorkflowOverlayJson | null
}) {
  const overlay = useVersionOverlay(workflowId, row)
  const diff = diffOverlayRefs(overlay ?? null, currentOverlay)
  return (
    <div className="mt-2 space-y-1">
      {diff.added.map((ref) => (
        <p key={ref} className="text-success">+ {ref}</p>
      ))}
      {diff.changed.map((ref) => (
        <p key={ref} className="text-warning">~ {ref}</p>
      ))}
      {diff.removed.map((ref) => (
        <p key={ref} className="text-destructive">− {ref}</p>
      ))}
      {diff.added.length + diff.changed.length + diff.removed.length === 0 && (
        <p className="text-muted-foreground">Same customization as the current version.</p>
      )}
    </div>
  )
}
